/**
 * I tesserini che risultano saliti due volte sulla stessa direzione.
 *
 * Nasce da una segnalazione dell'ufficio: dei ragazzi si passano il QR,
 * e lo stesso abbonamento finisce a bordo di due mezzi diversi sulla
 * stessa andata. Un abbonamento pagato, due persone trasportate.
 *
 * Due casi hanno un peso molto diverso, e la pagina deve distinguerli.
 * Se il tesserino e' l'unico a fare quell'incrocio, l'anomalia e' il
 * tesserino. Se lo fanno in sei, l'anomalia sono le due corse: un mezzo
 * si e' riempito e i ragazzi sono passati sull'altro. Per questo il
 * conteggio in evidenza e' quello dei casi "da solo".
 *
 * Non e' una prova, ed e' importante che la pagina lo dica: resta la
 * lista di chi vale la pena chiamare.
 */
import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import {
  Copy, Download, RefreshCw, ChevronDown, ChevronRight, Phone,
  Bus, Clock, UserCircle, ShieldCheck,
} from 'lucide-react';

const iso = (d) => {
  // Non toISOString: converte in UTC e di notte sposta il giorno.
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const g = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${g}`;
};

const giorniFa = (quanti) => {
  const d = new Date();
  d.setDate(d.getDate() - quanti);
  return iso(d);
};

const dataIT = (valore) => {
  if (!valore) return '';
  const [a, m, g] = valore.split('-');
  return `${g}/${m}/${a}`;
};

const DIREZIONE = { ANDATA: 'Andata', RITORNO: 'Ritorno' };

/**
 * Zero minuti non e' un dettaglio: il tesserino non ha fatto il giro a
 * piedi fra i due mezzi, quindi il QR esiste in copia — una foto.
 */
const descriviDistanza = (minuti) => {
  if (typeof minuti !== 'number') return '';
  if (minuti === 0) return 'nello stesso minuto, quindi in copia';
  if (minuti === 1) return 'a un minuto';
  return `a ${minuti} minuti`;
};

const PERIODI = [
  { giorni: 7, label: '7 giorni' },
  { giorni: 30, label: '30 giorni' },
  { giorni: 90, label: '90 giorni' },
];

const TesseriniCondivisi = () => {
  const [dal, setDal] = useState(giorniFa(30));
  const [al, setAl] = useState(iso(new Date()));
  const [gruppo, setGruppo] = useState('');
  const [gruppi, setGruppi] = useState([]);
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(true);
  const [aperti, setAperti] = useState({});
  // Di default si vede tutto: nascondere i casi di gruppo senza dirlo
  // farebbe sparire righe che l'ufficio ha visto il giorno prima.
  const [soloForti, setSoloForti] = useState(false);

  useEffect(() => {
    getAll('/trasporti/linee/').then(setGruppi).catch(() => setGruppi([]));
  }, []);

  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const { data } = await api.get('/controlli/eventi/tesserini_condivisi/', {
        params: { dal, al, ...(gruppo ? { linea: gruppo } : {}) },
      });
      setDati(data);
    } catch (errore) {
      console.error('Errore tesserini condivisi:', errore);
      notify.error('Non riesco a caricare l\'elenco');
      setDati(null);
    } finally {
      setCaricamento(false);
    }
  }, [dal, al, gruppo]);

  useEffect(() => { carica(); }, [carica]);

  const scegliPeriodo = (giorni) => {
    setDal(giorniFa(giorni));
    setAl(iso(new Date()));
  };

  const tuttiICasi = dati?.casi || [];
  const casi = soloForti
    ? tuttiICasi.filter((c) => c.forza === 'ALTA')
    : tuttiICasi;
  const tutti = dati?.tesserini || [];
  const tesserini = soloForti ? tutti.filter((t) => t.soli > 0) : tutti;
  const diGruppo = tuttiICasi.length - tuttiICasi.filter((c) => c.forza === 'ALTA').length;

  // Si scarica quello che si vede: con il filtro attivo il CSV non deve
  // riportarsi dentro i casi di gruppo.
  const scarica = () => {
    if (!casi.length) return notify.info('Non c\'è niente da scaricare.');
    const righe = [[
      'Tesserino', 'Intestatario', 'Telefono', 'Gruppo', 'Data',
      'Direzione', 'Era solo', 'Altri tesserini', 'Minuti fra le salite',
      'Ora', 'Mezzo', 'Autista',
    ]];
    casi.forEach((caso) => {
      caso.salite.forEach((s) => {
        righe.push([
          caso.codice, caso.intestatario, caso.telefono, caso.gruppo,
          dataIT(caso.data), DIREZIONE[caso.tipo_corsa] || caso.tipo_corsa,
          caso.forza === 'ALTA' ? 'si' : 'no', caso.compagni,
          caso.distanza_minuti,
          s.ora, s.mezzo, s.autista,
        ]);
      });
    });
    // Punto e virgola e BOM: Excel in italiano lo apre senza chiedere
    // niente e con gli accenti giusti.
    const csv = '﻿' + righe
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `tesserini-condivisi-${dal}_${al}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };


  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl pb-16">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Copy className="w-6 h-6 text-gray-400" />
              Tesserini condivisi
            </h1>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Abbonamenti saliti due volte sulla stessa direzione, nello stesso
              giro di mezzi, su viaggi che non possono essere lo stesso. Quando
              il tesserino è l'unico a fare quell'incrocio è il segno che il QR
              è stato passato a qualcuno.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={carica}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${caricamento ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
            {casi.length > 0 && (
              <button
                onClick={scarica}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:border-gray-900 transition text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Scarica
              </button>
            )}
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Dal</label>
              <input
                type="date" value={dal} onChange={(e) => setDal(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Al</label>
              <input
                type="date" value={al} onChange={(e) => setAl(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Gruppo</label>
              <select
                value={gruppo} onChange={(e) => setGruppo(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-900"
              >
                <option value="">Tutti</option>
                {gruppi.map((g) => (
                  <option key={g.id} value={g.id}>{g.codice}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-1.5">
              {PERIODI.map((p) => (
                <button
                  key={p.giorni}
                  onClick={() => scegliPeriodo(p.giorni)}
                  className="px-2.5 py-2 border border-gray-200 rounded-lg hover:border-gray-900 transition text-xs font-medium text-gray-600"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {diGruppo > 0 && (
            <label className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={soloForti}
                onChange={(e) => setSoloForti(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300"
              />
              <span>
                Nascondi i travasi di gruppo
                <span className="text-gray-400">
                  {' '}— {diGruppo} {diGruppo === 1 ? 'caso' : 'casi'} in cui lo
                  stesso incrocio riguarda più tesserini: lì quasi sempre è un
                  mezzo che si è riempito, non un QR passato
                </span>
              </span>
            </label>
          )}
        </div>

        {caricamento ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : casi.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
            <ShieldCheck className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <h2 className="font-semibold text-gray-900">
              {soloForti && diGruppo > 0
                ? 'Nessun caso isolato'
                : 'Nessun tesserino condiviso'}
            </h2>
            <p className="text-sm text-gray-500 mt-1.5 max-w-lg mx-auto">
              {soloForti && diGruppo > 0 ? (
                <>
                  Fra il {dataIT(dal)} e il {dataIT(al)} tutti i {diGruppo} casi
                  riguardano incroci fatti da più tesserini insieme: sono mezzi
                  che si sono scambiati i ragazzi, non QR passati. Togli il
                  filtro per vederli.
                </>
              ) : (
                <>
                  Fra il {dataIT(dal)} e il {dataIT(al)} nessun abbonamento
                  risulta salito due volte nello stesso giro di mezzi. Una
                  seconda scansione sullo stesso viaggio — o sulla stessa corsa
                  riavviata — non conta: è lo stesso ragazzo ripassato.
                </>
              )}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-5">
              <div className="px-4 py-3 border-b border-gray-200">
                <span className="font-semibold text-gray-900">
                  {tesserini.length} {tesserini.length === 1 ? 'tesserino' : 'tesserini'}
                </span>
                <span className="text-gray-500 text-sm">
                  {' · '}{casi.length} {casi.length === 1 ? 'caso' : 'casi'} fra
                  il {dataIT(dal)} e il {dataIT(al)}
                </span>
              </div>

              <ul className="divide-y divide-gray-100">
                {tesserini.map((t) => {
                  const suoi = casi.filter((c) => c.abbonamento === t.abbonamento);
                  const aperto = !!aperti[t.abbonamento];
                  return (
                    <li key={t.abbonamento}>
                      <button
                        onClick={() => setAperti((a) => ({ ...a, [t.abbonamento]: !aperto }))}
                        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition"
                      >
                        {aperto
                          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-gray-900 truncate">
                            {t.intestatario || 'Senza intestatario'}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {t.codice}
                            {t.gruppo ? ` · ${t.gruppo}` : ''}
                            {t.telefono ? ` · ${t.telefono}` : ''}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {/* Il numero che conta e' quello dei casi in cui era
                              solo: sugli altri si telefonerebbe a mezza classe. */}
                          <div className={`text-sm font-semibold ${t.soli > 0 ? 'text-orange-700' : 'text-gray-500'}`}>
                            {t.soli > 0
                              ? `${t.soli} ${t.soli === 1 ? 'volta' : 'volte'} da solo`
                              : 'solo in gruppo'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t.casi} {t.casi === 1 ? 'caso' : 'casi'}
                            {' · ultimo '}{dataIT(t.ultima)}
                          </div>
                        </div>
                      </button>

                      {aperto && (
                        <div className="px-4 pb-4 pl-11 space-y-3">
                          {t.telefono && (
                            <a
                              href={`tel:${t.telefono}`}
                              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition"
                            >
                              <Phone className="w-3.5 h-3.5" />
                              {t.telefono}
                            </a>
                          )}
                          {suoi.map((caso, indice) => (
                            <div
                              key={`${caso.data}-${caso.tipo_corsa}-${indice}`}
                              className={`border rounded-lg overflow-hidden ${
                                caso.forza === 'ALTA'
                                  ? 'border-orange-200'
                                  : 'border-gray-200'
                              }`}
                            >
                              <div className={`px-3 py-2 border-b text-sm font-medium text-gray-900 ${
                                caso.forza === 'ALTA'
                                  ? 'bg-orange-50 border-orange-100'
                                  : 'bg-gray-50 border-gray-100'
                              }`}>
                                {dataIT(caso.data)}
                                {' · '}
                                {DIREZIONE[caso.tipo_corsa] || caso.tipo_corsa}
                                <span className="text-gray-500 font-normal">
                                  {' · '}{caso.quante} mezzi
                                  {descriviDistanza(caso.distanza_minuti)
                                    ? ` · ${descriviDistanza(caso.distanza_minuti)}`
                                    : ''}
                                </span>
                                <div className="font-normal mt-1">
                                  {caso.forza === 'ALTA' ? (
                                    <span className="text-xs text-orange-800">
                                      Nessun altro tesserino ha fatto questo
                                      incrocio: è il tesserino a essere strano.
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-500">
                                      Altri {caso.compagni} tesserini hanno fatto
                                      lo stesso incrocio: probabilmente i ragazzi
                                      sono passati da un mezzo all'altro.
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ul className="divide-y divide-gray-100">
                                {caso.salite.map((s, i) => (
                                  <li key={i} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                    <span className="flex items-center gap-1.5 font-semibold text-gray-900 tabular-nums">
                                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                                      {s.ora}
                                    </span>
                                    {s.mezzo && (
                                      <span className="flex items-center gap-1.5 text-gray-600">
                                        <Bus className="w-3.5 h-3.5 text-gray-400" />
                                        {s.mezzo}
                                      </span>
                                    )}
                                    {s.autista && (
                                      <span className="flex items-center gap-1.5 text-gray-600">
                                        <UserCircle className="w-3.5 h-3.5 text-gray-400" />
                                        {s.autista}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-700 mb-1">Prima di chiamare qualcuno</p>
              <p>
                Questo elenco dice che un tesserino è stato letto su due viaggi
                che non possono essere lo stesso, a poca distanza di tempo. Non
                è una prova: un ragazzo trasbordato lungo la strada, o un
                autista che apre la corsa con un altro account, finiscono qui
                dentro allo stesso modo.
              </p>
              <p className="mt-2">
                Due cose, invece, non entrano affatto in elenco: le scansioni
                ripetute sullo stesso viaggio (è il ragazzo ripassato al
                controllo) e le corse rimaste aperte dal mattino al pomeriggio,
                che facevano sembrare due andate quello che era un'andata e un
                ritorno. Una terza resta, ma marcata: gli incroci fatti da più
                tesserini insieme. Il caso su cui vale la pena telefonare è
                quello in cui il tesserino era solo, meglio ancora se si ripete.
              </p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default TesseriniCondivisi;
